import { Badge, FingeringData, FingeringBeat } from '../types/fingeringEditor';

export const NOTE_Y_SPACING = 8;
/** エディタ表示用のY下方オフセット（annotate_pdf.py は 10.0） */
const OFFSET_BELOW = 14.0;

function groupByBeat(badges: Badge[]): Map<number, Badge[]> {
  const map = new Map<number, Badge[]>();
  for (const b of badges) {
    if (b.beatIndex !== null) {
      if (!map.has(b.beatIndex)) map.set(b.beatIndex, []);
      map.get(b.beatIndex)!.push(b);
    }
  }
  return map;
}

export function beatsToBadges(data: FingeringData): Badge[] {
  let nextId = 1;
  const badges: Badge[] = [];
  data.beats.forEach((beat, beatIndex) => {
    const sortedNotes = [...beat.notes].sort((a, b) => a.string - b.string);
    sortedNotes.forEach((note, noteIndex) => {
      badges.push({
        id: nextId++,
        x: beat.x,
        y: beat.y_bottom + OFFSET_BELOW - noteIndex * NOTE_Y_SPACING,
        note: note.pitch,
        string: note.string,
        fret: note.fret,
        status: 'auto',
        beatIndex,
        noteIndex,
        page: beat.page ?? 0,
      });
    });
  });
  return badges;
}

export function resortChordNoteIndexes(badges: Badge[]): Badge[] {
  const beatGroups = groupByBeat(badges);

  const noteIndexMap = new Map<number, number>();
  beatGroups.forEach((group) => {
    if (group.length <= 1) return;
    group.sort((a, b) => (a.string ?? 99) - (b.string ?? 99));
    group.forEach((badge, i) => {
      noteIndexMap.set(badge.id, i);
    });
  });

  if (noteIndexMap.size === 0) return badges;

  return badges.map((b) => {
    const newIndex = noteIndexMap.get(b.id);
    if (newIndex !== undefined && newIndex !== b.noteIndex) {
      const anchorY = b.y + (b.noteIndex ?? 0) * NOTE_Y_SPACING;
      return { ...b, noteIndex: newIndex, y: anchorY - newIndex * NOTE_Y_SPACING };
    }
    return b;
  });
}

export function badgesToBeats(badges: Badge[], originalBeats: FingeringBeat[]): FingeringBeat[] {
  const addedBadges: Badge[] = [];
  const validBadges: Badge[] = [];
  for (const badge of badges) {
    if (badge.string == null || badge.fret == null) continue;
    if (badge.beatIndex !== null) {
      validBadges.push(badge);
    } else {
      addedBadges.push(badge);
    }
  }
  const beatGroups = groupByBeat(validBadges);
  const result: FingeringBeat[] = [];
  beatGroups.forEach((group: Badge[], beatIndex: number) => {
    const original = originalBeats[beatIndex];
    const sorted = group.sort((a: Badge, b: Badge) => (a.noteIndex ?? 0) - (b.noteIndex ?? 0));
    // noteIndex=0のバッジ座標からbeat座標を逆算（ドラッグ移動を反映）
    const baseBadge = sorted.find((b) => b.noteIndex === 0) ?? sorted[0];
    const baseNoteIndex = baseBadge.noteIndex ?? 0;
    const newX = baseBadge.x;
    const newYBottom = baseBadge.y - OFFSET_BELOW + baseNoteIndex * NOTE_Y_SPACING;
    const notes = sorted.map((b: Badge) => ({ pitch: b.note, string: b.string ?? 1, fret: b.fret ?? 0 }));
    if (original) {
      result.push({
        measure: original.measure, x: newX, y_bottom: newYBottom,
        page: original.page ?? 0, position: original.position,
        notes,
      });
    } else {
      // 合成 beatIndex（originalBeats 範囲外）: 追加モードで新規生成された和音
      result.push({
        measure: -1, x: newX, y_bottom: newYBottom,
        page: baseBadge.page, position: 0,
        notes,
      });
    }
  });
  for (const badge of addedBadges) {
    result.push({
      measure: -1, x: badge.x, y_bottom: badge.y, page: badge.page, position: 0,
      notes: [{ pitch: badge.note, string: badge.string ?? 1, fret: badge.fret ?? 0 }],
    });
  }
  return result;
}
